// ============================================================================
// Raise an escalation — the port of the legacy delivery-v2 drawer. Pick the issue,
// add what you know, and the teams who can fix it get it on WhatsApp while a CRM
// deal opens against the order.
//
// One drawer, three TARGETS: a BOOKING on the logistics board, a warehouse queue
// ROW (pending picklist / pending GRN), and a MISSING ORDERS row on Last Mile. Same
// three legs, same append-only log, same issue catalogue — the only difference is
// what the escalation is keyed on, and the last two cannot be keyed on a booking
// at all: a PICK leg books no truck, nor does a vendor-delivered inbound, and a
// Missing Orders row's whole complaint is that NO truck was ever created. So those two
// key on the STAGE. That is why this takes a `target` union rather than being
// copied per surface.
//
// This does NOT change the order. Cancelling, postponing, re-sourcing and the
// rest are deliberate mutations with their own actions on the order page; this
// is the "somebody needs to look at this" signal, nothing more.
//
// The issue list comes from the backend (one config drives both the options and
// the WhatsApp routing) rather than being restated here — the legacy drawer
// hard-coded its list and quietly fell three issues behind the server's.
// ============================================================================
import { Alert, Drawer, Form, Input, InputNumber, Select, Space, Button, Table, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import {
  IssueCategory,
  MissingOrderRow,
  PendingQueueKind,
  PendingStageRow,
  listIssueTypes,
  raiseLogisticsIssue,
  raiseStageIssue,
} from "../logistics.api";
import { BookingEscalation, LogisticsOrderRow } from "../../types";
import ExistingTicketsBanner from "./existing_tickets_banner";
import {
  ReturnStageItem,
  markQcComplete,
  raiseReturnShortfallAtPickup,
} from "../../returns/returns.api";

// Issue types that require a per-item quantity form instead of free-text remarks.
// Keyed off the backend-authored issue label, which must match settings.py exactly.
const SHORTFALL_AT_PICKUP = "Shortfall quantity";

const RETURN_ONLY_CATEGORIES = ["return_pickup"];

// What is being escalated: a truck on the board, a row on one of the two warehouse work queues
// (named by the queue it is stuck in, since that is what the WhatsApp groups need to hear), or a
// MISSING ORDERS row off the Last Mile section — a delivery promised today or earlier that never got a
// truck.
//
// The last two share a mechanism for the same reason: neither has a booking to key on. A PICK leg
// books no truck, and a Missing Orders row's whole problem is that nothing was booked — so both key the
// escalation on the STAGE, and a Missing Orders row's `id` IS its delivery leg's stage id.
export type IssueTarget =
  | { kind: "booking"; row: LogisticsOrderRow }
  | { kind: "stage"; row: PendingStageRow; queue: PendingQueueKind }
  | { kind: "missing"; row: MissingOrderRow };

interface Props {
  target: IssueTarget | null; // non-null opens the drawer
  onClose: () => void;
  // "It landed." The board parent refetches (the operation log gained an entry); the queue panel
  // merges the returned escalation into the row it already has, so it arrives here.
  onRaised: (escalation: BookingEscalation | null) => void;
  // Optional: narrow the issue catalogue to a subset of category keys (e.g. ["return_pickup"] for
  // the Returns tab, so ops only sees reasons that apply to a return pickup — not every forward-leg
  // issue type). Omit to show every category except the return-only ones (board + warehouse queue default).
  categoryFilter?: string[];
  // Return-stage items for the "Shortfall quantity" per-item form. Only consulted when that
  // issue is picked; omit when the surface has no item list to show.
  items?: ReturnStageItem[];
  // Return stage id — needed to post the shortfall-at-pickup payload. Omit when the drawer
  // is mounted outside the Returns surface (board / warehouse queue).
  returnStageId?: number;
  // Goods already received at the WH: pickup qty is frozen, so a shortfall is recorded at QC.
  returnReceived?: boolean;
}

const titleFor = (t: IssueTarget): string => {
  if (t.kind === "booking") return `Raise escalation — booking #${t.row.id}`;
  // A Missing Orders row is named by WHAT IS WRONG, not by a queue it is sitting in — it is in no queue,
  // which is the problem. The reason is the first thing the group reading this needs.
  if (t.kind === "missing") return `Raise escalation — Missing Orders (${t.row.reason})`;
  return `Raise escalation — ${t.queue === "grn" ? "pending GRN" : "pending picklist"}`;
};

const subtitleFor = (t: IssueTarget): React.ReactNode => {
  if (t.kind === "missing") {
    return (
      <>
        {t.row.po_number || "—"} · {t.row.lead_id || "—"}
        <br />
        {/* The promise and the blocker, together: the two facts that make this escalation
            actionable without opening the order. */}
        Promised {t.row.target_delivery_date || "—"}
        {typeof t.row.days_late === "number" && t.row.days_late > 0
          ? ` · ${t.row.days_late}d late`
          : " · due today"}
        <br />
        {t.row.client_name || "—"} · {t.row.fulfilment_warehouse_name || "—"}
      </>
    );
  }
  return t.kind === "booking" ? (
    <>
      {t.row.po_numbers?.join(", ") || "—"} · {t.row.client_name || "—"}
      <br />
      {t.row.origin_label || "—"} → {t.row.destination_label || "—"}
    </>
  ) : (
    <>
      {t.row.po_number || "—"} · {t.row.lead_id || "—"}
      <br />
      {/* The dock the work is owed at, and the party it is owed to/from — an escalation the group
          reading it cannot place is one nobody can act on. */}
      {t.row.warehouse_name || "—"} ·{" "}
      {(t.queue === "grn" ? t.row.supplier_name : t.row.client_name) || "—"}
    </>
  );
};

const IssueDrawer: React.FC<Props> = ({
  target,
  onClose,
  onRaised,
  categoryFilter,
  items,
  returnStageId,
  returnReceived,
}) => {
  const [form] = Form.useForm();
  const [categories, setCategories] = useState<IssueCategory[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const issueType = Form.useWatch("issue_type", form);
  const visibleCategories = categoryFilter?.length
    ? categories.filter((c) => categoryFilter.includes(c.category))
    : categories.filter((c) => !RETURN_ONLY_CATEGORIES.includes(c.category));
  const isShortfall = issueType === SHORTFALL_AT_PICKUP;

  // Build the per-item form rows every time the drawer re-opens, prefilled with picked_qty.
  const shortfallRows = useMemo(
    () =>
      (items ?? []).map((it) => ({
        allocation_item_id: it.allocation_item_id,
        product_name: it.product_name,
        picked_qty: Number(it.picked_qty) || 0,
      })),
    [items]
  );

  // Fetched once the drawer first opens, then cached for the session.
  useEffect(() => {
    if (target && categories.length === 0) listIssueTypes().then(setCategories);
  }, [target, categories.length]);

  useEffect(() => {
    if (target) form.resetFields();
  }, [target, form]);

  // When the shortfall issue is picked, seed each row's actual_qty with its picked_qty so the
  // operator only has to edit the lines that actually fell short.
  useEffect(() => {
    if (isShortfall) {
      const seed: Record<string, number> = {};
      shortfallRows.forEach((r) => {
        seed[`actual_${r.allocation_item_id}`] = r.picked_qty;
      });
      form.setFieldsValue(seed);
    }
  }, [isShortfall, shortfallRows, form]);

  const handleSubmit = async (values: Record<string, any>) => {
    if (!target) return;
    setSubmitting(true);

    // Shortfall takes a dedicated endpoint that shrinks per-item OperationItem qty + raises
    // a RETURN_SHORT_AT_PICKUP escalation in one transaction. Falls back to the generic
    // raise path if the surface didn't give us a return stage id to post against.
    if (isShortfall && returnStageId && returnReceived) {
      const received_qty: Record<string, number> = {};
      shortfallRows.forEach((r) => {
        received_qty[String(r.allocation_item_id)] = values[`actual_${r.allocation_item_id}`] ?? r.picked_qty;
      });
      const ok = await markQcComplete(returnStageId, {
        received_qty,
        note: values.remarks?.trim() || undefined,
      });
      setSubmitting(false);
      if (ok) {
        form.resetFields();
        onClose();
        onRaised(null);
      }
      return;
    }

    if (isShortfall && returnStageId) {
      const payload = {
        items: shortfallRows.map((r) => ({
          allocation_item_id: r.allocation_item_id,
          actual_qty: values[`actual_${r.allocation_item_id}`] ?? r.picked_qty,
        })),
        note: values.remarks?.trim() || undefined,
      };
      const ok = await raiseReturnShortfallAtPickup(returnStageId, payload);
      setSubmitting(false);
      if (ok) {
        form.resetFields();
        onClose();
        onRaised(null);
      }
      return;
    }

    const body = {
      issue_type: values.issue_type,
      remarks: values.remarks?.trim() || undefined,
    };
    // The booking call answers the board's own refetch, so it has no escalation to hand back; the
    // stage call does, because the lists that use it merge rather than refetch. A Missing Orders row goes
    // down the stage arm unchanged — its `id` already IS the delivery leg's stage id.
    const result =
      target.kind === "booking"
        ? { ok: await raiseLogisticsIssue(target.row.id, body), escalation: null }
        : await raiseStageIssue(target.row.id, body);
    setSubmitting(false);
    if (result.ok) {
      form.resetFields();
      onClose();
      onRaised(result.escalation);
    }
  };

  return (
    <Drawer
      title={target ? titleFor(target) : ""}
      open={!!target}
      onClose={onClose}
      width={440}
      destroyOnClose
    >
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        {target ? subtitleFor(target) : null}
      </Typography.Paragraph>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="This alerts the team — it does not change the order."
        description={
          target && target.kind !== "booking"
            ? "The right WhatsApp groups get the details and a CRM escalation opens against this order. The row stays on this list — the work is still owed — until it is done or somebody resolves the escalation."
            : "The right WhatsApp groups get the details and a CRM escalation opens against this order. To actually cancel, postpone or re-source anything, use the actions on the order page."
        }
      />

      {/* What's already been raised is one click away via the row's "View escalation notes" —
          not duplicated here. */}

      {target && (
        <ExistingTicketsBanner
          leadId={target.kind === "booking" ? null : target.row.lead_id}
          poNumber={
            target.kind === "booking" ? target.row.po_numbers?.[0] : target.row.po_number
          }
          issueType={issueType}
        />
      )}

      <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false}>
        <Form.Item
          name="issue_type"
          label="What's the problem?"
          rules={[{ required: true, message: "Pick the issue" }]}
        >
          <Select
            placeholder="Select an issue"
            options={visibleCategories.map((c) => ({
              label: c.label,
              options: c.issues.map((i) => ({ value: i, label: i })),
            }))}
          />
        </Form.Item>
        {isShortfall && (
          shortfallRows.length === 0 ? (
            <Alert
              type="warning"
              style={{ marginBottom: 16 }}
              message="No items available on this stage to shortfall."
              description="Reload the Returns tab; if this persists the stage was created without items."
            />
          ) : (
            <Form.Item
              label="Edit the actual picked quantity for each item"
              tooltip="Prefilled with the originally requested qty. Lower it only on the lines that fell short."
            >
              <Table<typeof shortfallRows[number]>
                size="small"
                pagination={false}
                rowKey="allocation_item_id"
                dataSource={shortfallRows}
                columns={[
                  {
                    title: "Item",
                    dataIndex: "product_name",
                    key: "product_name",
                    render: (v: string) => <Typography.Text>{v}</Typography.Text>,
                  },
                  {
                    title: "Requested",
                    dataIndex: "picked_qty",
                    key: "picked_qty",
                    width: 90,
                    render: (v: number) => <Typography.Text type="secondary">{v}</Typography.Text>,
                  },
                  {
                    title: "Actual",
                    key: "actual",
                    width: 110,
                    render: (_, r) => (
                      <Form.Item
                        name={`actual_${r.allocation_item_id}`}
                        noStyle
                        rules={[
                          { required: true, message: "" },
                          {
                            validator: (_rule, val) =>
                              val === undefined || val === null || val === ""
                                ? Promise.reject(new Error("Required"))
                                : Number(val) < 0
                                ? Promise.reject(new Error("Must be ≥ 0"))
                                : Number(val) > r.picked_qty
                                ? Promise.reject(new Error("Can't exceed requested"))
                                : Promise.resolve(),
                          },
                        ]}
                      >
                        <InputNumber
                          min={0}
                          max={r.picked_qty}
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    ),
                  },
                ]}
              />
            </Form.Item>
          )
        )}

        <Form.Item
          name="remarks"
          label={isShortfall ? "Reason for the shortfall (optional)" : "Anything else the team should know?"}
          tooltip="Goes into the WhatsApp message and the CRM escalation as-is."
        >
          <Input.TextArea rows={3} maxLength={500} showCount placeholder="Optional" />
        </Form.Item>
        <Space>
          <Button
            type="primary"
            danger
            htmlType="submit"
            loading={submitting}
            disabled={isShortfall && shortfallRows.length === 0}
          >
            {isShortfall ? "Report shortfall" : "Raise escalation"}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </Space>
      </Form>
    </Drawer>
  );
};

export default IssueDrawer;
